/**
 * services/PlaybookService.ts
 * Service for managing trading playbooks
 *
 * Playbooks define specific trading strategies with criteria, confluences,
 * and entry models. They are grouped under strategies via Strategy.playbookIds.
 *
 * Extends BaseService to provide CRUD operations with status-based filtering
 * and strategy-based queries that preserve playbook ordering.
 */

import { BaseService } from './BaseService';
import { db } from '@/lib/db';
import type { Playbook } from '@/types/schema/Playbook';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { isEntityActive } from '@/types/shared/EntityStatus';

/**
 * Service for managing trading playbooks
 *
 * Provides CRUD operations plus status-based filtering and strategy linkage.
 * Inherits getAll(), get(), update(), softDelete() from BaseService.
 */
export class PlaybookService extends BaseService<Playbook> {
  constructor() {
    super('playbooks');
  }

  /**
   * Add a new playbook with default status
   *
   * Defaults to status: 'active' if not provided.
   *
   * @param data - Partial playbook data
   * @returns Promise resolving to the created playbook
   */
  override async add(data: Partial<Playbook>): Promise<Playbook> {
    return super.add({ status: 'active', ...data });
  }

  /**
   * Get playbooks by status
   *
   * Filters playbooks by the specified EntityStatus.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param status - The status to filter by ('active' | 'inactive' | 'archived')
   * @returns Promise resolving to array of playbooks with matching status
   */
  async getByStatus(status: EntityStatus): Promise<Playbook[]> {
    const allPlaybooks = await this.getAll();
    return allPlaybooks.filter(playbook => playbook.status === status);
  }

  /**
   * Get all active playbooks
   *
   * Convenience method for getting playbooks where status is 'active'.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @returns Promise resolving to array of active playbooks
   */
  async getActivePlaybooks(): Promise<Playbook[]> {
    const allPlaybooks = await this.getAll();
    return allPlaybooks.filter(playbook => isEntityActive(playbook.status));
  }

  /**
   * Get playbooks linked to a strategy, preserving order
   *
   * Fetches playbooks by reading Strategy.playbookIds[] (source of truth)
   * and returns them in the same order as defined in the strategy.
   *
   * @param strategyId - The strategy ID to get playbooks for
   * @returns Promise resolving to array of playbooks in strategy order (empty if strategy not found)
   */
  async getByStrategyId(strategyId: string): Promise<Playbook[]> {
    // Fetch strategy to get playbookIds (source of truth)
    const strategy = await db.strategies.get(strategyId);
    if (!strategy || !strategy.playbookIds || strategy.playbookIds.length === 0) {
      return [];
    }

    // Get all playbooks and create lookup map for O(1) access
    const allPlaybooks = await this.getAll();
    const playbookMap = new Map(allPlaybooks.map(p => [p.id, p]));

    // Return playbooks in the order defined by strategy.playbookIds
    const orderedPlaybooks: Playbook[] = [];
    for (const id of strategy.playbookIds) {
      const playbook = playbookMap.get(id);
      if (playbook) {
        orderedPlaybooks.push(playbook);
      }
    }

    return orderedPlaybooks;
  }
}

// Export singleton instance
export const playbookService = new PlaybookService();
